import { useEffect, useRef, useState } from "react"
import useAuth0 from "../components/auth/useAuth0"

const DEFAULT_OPTIONS = {
  defaultValue: null,
  condition: true,
  getter: ({ data }) => data,
  cacheKey: null,
}

const requestCache = new Map()

/**
 * @param {Function} callback
 * @param dependencies
 * @param options
 * @returns {{isLoading: boolean, isError: boolean, reload: (function(): void), data: any, error: any, isSuccess: boolean}}
 */
const useRequest = (callback, dependencies = [], options = DEFAULT_OPTIONS) => {
  const { defaultValue, condition, getter, cacheKey } = {
    ...DEFAULT_OPTIONS,
    ...options,
  }

  const cached = cacheKey ? requestCache.get(cacheKey) : undefined
  const hasCached = cached !== undefined

  const [data, setData] = useState(hasCached ? cached : defaultValue)
  const [isLoading, setIsLoading] = useState(!hasCached)
  const [isSuccess, setIsSuccess] = useState(hasCached)
  const [isError, setIsError] = useState(false)
  const [error, setError] = useState(null)
  const [errorCode, setErrorCode] = useState(null)
  const { isAuthenticated } = useAuth0()

  const isManualReload = useRef(false)

  const fetch = () => {
    if (!isAuthenticated) return
    if (!condition) {
      setIsLoading(false)
      setData(defaultValue)
      return
    }

    if (hasCached && !isManualReload.current) return

    isManualReload.current = false

    let shouldUpdate = true

    setError(null)
    setIsError(false)
    setIsLoading(true)
    callback()
      .then((result) => {
        if (shouldUpdate) {
          const value = getter(result)
          if (cacheKey) requestCache.set(cacheKey, value)
          setData(value)
          setIsSuccess(true)
        }
      })
      .catch((err) => {
        setIsError(true)
        if (err.response) {
          setError(err.response.data)
          setErrorCode(err.response.status)
        }
        setData(defaultValue)
      })
      .finally(() => {
        if (shouldUpdate) setIsLoading(false)
      })

    return () => {
      shouldUpdate = false
    }
  }

  useEffect(fetch, [...dependencies, isAuthenticated])

  const reload = () => {
    if (cacheKey) requestCache.delete(cacheKey)
    isManualReload.current = true
    fetch()
  }

  return {
    data,
    setData,
    isLoading: !isAuthenticated || isLoading,
    isSuccess,
    isError,
    error,
    errorCode,
    reload,
  }
}

export default useRequest
